import { useMemo } from "react";

import { pick } from "ramda";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";

import type {
	BaseCreateType,
	ClubModel,
	CompetitionClubsTeamsModel,
	CompetitionCreateDivisionModel,
	CompetitionCreatedFieldReservationModel,
	CompetitionFieldWithReservationModel,
	CompetitionGroupModel,
	CompetitionModel,
	CompetitionRemovedFieldReservationModel,
	CurrencyCode,
	FetchCompetitionTeamsParams,
	FullTeamModel,
	GetCompetitionPlayersByTeamsModel,
	GetCompetitionPlayersModel,
	GetCompetitionTeamModel,
	GroupStatusEnum,
	InvitationModel,
	InvitationStatus,
	LogoModel,
	MatchModel,
	MatchesFilterModel,
	MediaModel,
	NewFieldReservationModel,
	NewScoutModel,
	OrderStatus,
	PaginationParamsModel,
	PaginationTokenType,
	RefereeManagerModel,
	ScoutModel,
	StatItemModel,
	TeamFiltersModelWithId,
	TournamentDivisionModel,
	UpdateClubPlayerFormModal,
	UpdateFieldReservationModel
} from "shared/types";
import {
	competitionClubsTeamsResolver,
	competitionCreatedFieldReservationResolver,
	competitionFieldWithReservationResolver,
	competitionGroupResolver,
	competitionRemovedFieldReservationResolver,
	competitionWithScoutsResolver,
	followedCompetitionsResolver,
	getCompetitionPlayersByTeamsResolver,
	getCompetitionPlayersResolver,
	getCompetitionTeamResolver,
	paginationTokenResolver,
	statItemResolver,
	teamFiltersResolverWithId,
	tournamentDivisionResolver
} from "shared/types";

import BaseConverter from "./converters/baseConverter";

import type {
	CompetitionClubsTeamsFilterModel,
	CompetitionCustomFeeModel,
	CompetitionHealthModel,
	CompetitionMatchesHealthResultModel,
	FetchCompetitionDivisionsParams,
	GetCompetitionTeamPlayersModel,
	PlayersByTeamsFilterModel
} from "../types/CompetitionModel";
import {
	competitionHealthResolver,
	competitionMatchesHealthResultResolver,
	getCompetitionTeamPlayersResolver
} from "../types/CompetitionModel";
import type { FieldInfo } from "../types/FacilityModel";
import type { PartialPaymentSettingsModel, SplitPaymentSettingsModel } from "../types/PaymentModel";
import type { UpdateScoutModel } from "../types/ScoutModel";

const useCompetitionApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			findCompetitions: async ({
				clubId,
				search,
				maxMiles,
				limit
			}: {
				clubId?: string;
				search?: string;
				maxMiles?: number;
				limit?: number;
			}) => {
				return BaseConverter.convert<{ competitions: CompetitionModel[] }>(
					await actions.get(API_URLS.COMPETITION.FIND, {
						"as-club-id": clubId,
						search,
						"max-miles": maxMiles,
						limit
					})
				);
			},
			applyToCompetition: async ({
				clubId,
				competitionId,
				teams
			}: {
				clubId: string;
				competitionId: string;
				teams?: Partial<FullTeamModel>[];
			}) => {
				return BaseConverter.convert<{ club: { id: string }; competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.APPLY, {
						asClubId: clubId,
						competitionId,
						teams: teams?.length ? teams : []
					})
				);
			},
			getTeams: async (params: FetchCompetitionTeamsParams) => {
				const requestParams: {
					competitionId: string;
					clubId?: string;
					status?: InvitationStatus;
					ungrouped?: boolean;
					ages?: string | number | null;
					genders?: string;
					levels?: string | number;
					search?: string;
					requestGroup?: boolean;
				} = pick(["competitionId", "clubId", "status", "ungrouped", "search", "requestGroup"], params);

				if (params?.ages?.length) requestParams["ages"] = params.ages.join(",");
				if (params?.genders?.length) requestParams["genders"] = params.genders.join(",");
				if (params?.levels?.length) requestParams["levels"] = params.levels.join(",");

				return BaseConverter.convert<{ competition: GetCompetitionTeamModel }>(
					await actions.get(API_URLS.COMPETITION.TEAMS, params, {
						resolvers: [{ resolver: getCompetitionTeamResolver, fields: ["competition"] }]
					})
				);
			},
			getClubsWithTeams: async (params: {
				competitionId: string;
				withPayments?: boolean;
				filters?: CompetitionClubsTeamsFilterModel;
			}) => {
				return BaseConverter.convert<{ competition: CompetitionClubsTeamsModel }>(
					await actions.get(
						API_URLS.COMPETITION.LIST_TEAMS_BY_CLUBS,
						{
							competitionId: params.competitionId,
							withPayments: params.withPayments,
							clubId: params.filters?.clubId,
							ages: params?.filters?.ages?.length ? params.filters.ages.join(",") : undefined,
							genders: params?.filters?.genders?.length ? params.filters.genders.join(",") : undefined,
							levels: params?.filters?.levels?.length ? params.filters.levels.join(",") : undefined,
							status: params?.filters?.status,
							search: params?.filters?.search,
							clubAdminStatus: params?.filters?.clubAdminStatus?.length
								? params.filters.clubAdminStatus.join(",")
								: undefined,
							clubAdminsRosters: params?.filters?.clubAdminsRosters,
							teamRostersProgress: params?.filters?.teamRostersProgress?.length
								? params.filters.teamRostersProgress.join(",")
								: undefined,
							teamContactsStatus: params?.filters?.teamContactsStatus?.length
								? params.filters.teamContactsStatus.join(",")
								: undefined,
							teamHealth: params?.filters?.teamHealth
						},
						{
							resolvers: [{ resolver: competitionClubsTeamsResolver, fields: ["competition"] }]
						}
					)
				);
			},
			getClubs: async ({ competitionId, status }: { competitionId: string; status?: InvitationStatus }) => {
				return BaseConverter.convert<{ clubs: Pick<ClubModel, "id" | "name" | "status">[] }>(
					await actions.get(API_URLS.COMPETITION.CLUBS, {
						"competition-id": competitionId,
						status
					})
				);
			},
			getCompetitionMatches: async ({
				competitionId,
				filters,
				limit,
				before,
				after
			}: {
				competitionId: string;
				filters?: MatchesFilterModel;
			} & PaginationParamsModel) => {
				return BaseConverter.convert<{
					matches: MatchModel[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(API_URLS.COMPETITION.COMPETITION_MATCHES, {
						competitionId,
						status: !!filters?.status?.length ? filters.status.join(",") : undefined,
						divisionIds: !!filters?.divisionIds?.length ? filters.divisionIds.join(",") : undefined,
						facilityIds: !!filters?.facilityIds?.length ? filters.facilityIds.join(",") : undefined,
						fieldIds: !!filters?.fieldIds?.length ? filters.fieldIds.join(",") : undefined,
						scheduleStatus: filters?.scheduleStatus,
						overdueTime: filters?.overdueTime,
						ord: filters?.matchNumberSearch,
						search: filters?.teamSearch,
						limit,
						before,
						after
					})
				);
			},
			getCompetitionReferees: async (competitionId: string) => {
				return BaseConverter.convert<{ refereeOrganizations: RefereeManagerModel[] }>(
					await actions.get(API_URLS.COMPETITION.COMPETITION_REFEREES, {
						"competition-id": competitionId
					})
				);
			},
			getOverview: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: CompetitionModel }>(
					await actions.get(API_URLS.COMPETITION.GET_OVERVIEW, {
						"competition-id": competitionId
					})
				);
			},
			getInfo: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: CompetitionModel }>(
					await actions.get(API_URLS.COMPETITION.GET_INFO, {
						"competition-id": competitionId
					})
				);
			},
			getTeamFilters: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: TeamFiltersModelWithId }>(
					await actions.get(
						API_URLS.COMPETITION.GET_TEAM_FILTERS,
						{
							"competition-id": competitionId
						},
						{ resolvers: [{ resolver: teamFiltersResolverWithId, fields: ["competition"] }] }
					)
				);
			},
			updateCompetitionSettings: async (
				updateData: Omit<CompetitionModel, "id" | "league" | "type" | "logo"> & {
					logo?: string | null | LogoModel | MediaModel;
					competitionId: string;
					refereeOrganizations?: {
						add: { id: string }[];
						remove: { id: string }[];
					};
					registrationFee?: number;
					registrationFeeCurrency?: CurrencyCode;
					customRegistrationFees?: Partial<CompetitionCustomFeeModel>[];
					partialPayment?: null | PartialPaymentSettingsModel;
					splitPayment?: SplitPaymentSettingsModel[];
				}
			) => {
				return BaseConverter.convert(await actions.post(API_URLS.COMPETITION.UPDATE_SETTINGS, updateData));
			},
			listCompetitionFieldReservations: async (competitionId: string) => {
				return BaseConverter.convert<{ fields: CompetitionFieldWithReservationModel[] }>(
					await actions.get(
						API_URLS.COMPETITION.LIST_FIELD_RESERVATIONS,
						{
							competitionId
						},
						{
							resolvers: [
								{
									resolver: competitionFieldWithReservationResolver,
									fields: ["fields"]
								}
							]
						}
					)
				);
			},
			updateCompetitionFieldReservations: async (
				competitionId: string,
				reservations: {
					create: NewFieldReservationModel[];
					update: UpdateFieldReservationModel[];
					remove: { id: string }[];
				}
			) => {
				return BaseConverter.convert<{
					created: CompetitionCreatedFieldReservationModel[];
					removed: CompetitionRemovedFieldReservationModel[];
					updated: CompetitionRemovedFieldReservationModel[];
				}>(
					await actions.post(
						API_URLS.COMPETITION.UPDATE_FIELD_RESERVATIONS,
						{
							competitionId,
							reservations
						},
						{
							resolvers: [
								{
									resolver: competitionCreatedFieldReservationResolver,
									fields: ["created"]
								},
								{
									resolver: competitionRemovedFieldReservationResolver,
									fields: ["removed"]
								},
								{
									resolver: competitionRemovedFieldReservationResolver,
									fields: ["updated"]
								}
							]
						}
					)
				);
			},
			listCompetitionFacilityFields: async (competitionId: string) => {
				return BaseConverter.convert<{ fields: Partial<FieldInfo>[] }>(
					await actions.get(API_URLS.COMPETITION.LIST_COMPETITION_FACILITY_FIELDS, {
						competitionId
					})
				);
			},
			getCompetitionTeamStats: async (competitionId: string) => {
				return BaseConverter.convert<{ items: StatItemModel[] }>(
					await actions.get(
						API_URLS.COMPETITION.GET_COMPETITION_TEAM_STATS,
						{
							"competition-id": competitionId
						},
						{
							resolvers: [
								{
									resolver: statItemResolver,
									fields: ["items"]
								}
							]
						}
					)
				);
			},
			getCompetitionPlayerStats: async (competitionId: string) => {
				return BaseConverter.convert<{ items: StatItemModel[] }>(
					await actions.get(
						API_URLS.COMPETITION.GET_COMPETITION_PLAYER_STATS,
						{
							"competition-id": competitionId
						},
						{
							resolvers: [
								{
									resolver: statItemResolver,
									fields: ["items"]
								}
							]
						}
					)
				);
			},
			getCompetitionGroups: async (data: { competitionId: string; status?: GroupStatusEnum; withTeams?: boolean }) => {
				return BaseConverter.convert<{ groups: CompetitionGroupModel[] }>(
					await actions.get(
						API_URLS.COMPETITION.GET_COMPETITION_GROUPS,
						{
							"competition-id": data.competitionId,
							status: data.status,
							"with-teams": data.withTeams
						},
						{
							resolvers: [
								{
									resolver: competitionGroupResolver,
									fields: ["groups"]
								}
							]
						}
					)
				);
			},
			getFollowedCompetitions: async ({
				search,
				after
			}: {
				search?: string;
			} & PaginationParamsModel) => {
				return BaseConverter.convert<{
					competitions: BaseCreateType[];
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.COMPETITION.LIST_FOLLOWED_COMPETITIONS,
						{
							search: search || undefined,
							after: after || undefined
						},
						{
							resolvers: [
								{
									resolver: followedCompetitionsResolver
								}
							]
						}
					)
				);
			},
			getStructure: async (id: string) => {
				return BaseConverter.convert<{ competition: Partial<CompetitionModel> }>(
					await actions.get(API_URLS.COMPETITION.GET_STRUCTURE, {
						competitionId: id
					})
				);
			},
			getCompetitionDivisions: async ({
				competitionId,
				ages,
				levels,
				genders,
				search,
				limit,
				before,
				after
			}: FetchCompetitionDivisionsParams & PaginationParamsModel) => {
				return BaseConverter.convert<{ divisions: TournamentDivisionModel[] }>(
					await actions.get(
						API_URLS.COMPETITION.LIST_COMPETITION_DIVISIONS,
						{
							competitionId,
							ages: ages?.length ? ages.join(",") : undefined,
							genders: genders?.length ? genders.join(",") : undefined,
							levels: levels?.length ? levels.join(",") : undefined,
							search,
							limit,
							before,
							after
						},
						{
							resolvers: [
								{
									resolver: tournamentDivisionResolver,
									fields: ["divisions"]
								}
							]
						}
					)
				);
			},
			getCompetitionDivision: async (divisionId: string) => {
				return BaseConverter.convert<{ division: TournamentDivisionModel }>(
					await actions.get(
						API_URLS.COMPETITION.GET_COMPETITION_DIVISION,
						{
							divisionId
						}
						// {
						// 	resolvers: [
						// 		{
						// 			resolver: tournamentDivisionResolver,
						// 			fields: ["division"]
						// 		}
						// 	]
						// }
					)
				);
			},
			createDivisions: async (competitionId: string, divisions: CompetitionCreateDivisionModel[]) => {
				return BaseConverter.convert<{ divisions: TournamentDivisionModel[] }>(
					await actions.post(
						API_URLS.COMPETITION.CREATE_COMPETITION_DIVISIONS,
						{
							competitionId,
							divisions
						},
						{
							resolvers: [
								{
									resolver: tournamentDivisionResolver,
									fields: ["divisions"]
								}
							]
						}
					)
				);
			},
			updateDivision: async (competitionId: string, division: CompetitionCreateDivisionModel) => {
				return BaseConverter.convert<{ division: TournamentDivisionModel }>(
					await actions.post(
						API_URLS.COMPETITION.UPDATE_COMPETITION_DIVISION,
						{
							competitionId,
							division
						},
						{
							resolvers: [
								{
									resolver: tournamentDivisionResolver,
									fields: ["division"]
								}
							]
						}
					)
				);
			},
			deleteDivision: async (competitionId: string, divisionId: string) => {
				return BaseConverter.convert<{ division: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.DELETE_COMPETITION_DIVISION, {
						competitionId,
						divisionId
					})
				);
			},
			getFavoriteCompetitionDivisions: async () => {
				return BaseConverter.convert<{
					competition: null | Partial<CompetitionModel>;
					competitions: Partial<CompetitionModel>[];
					divisions: Partial<TournamentDivisionModel>[];
					groups: Partial<CompetitionGroupModel>[];
				}>(await actions.get(API_URLS.COMPETITION.LIST_FAVORITE_COMPETITION_DIVISIONS));
			},
			listScouts: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: { id: string; scouts: ScoutModel[] } }>(
					await actions.get(
						API_URLS.COMPETITION.LIST_SCOUTS,
						{
							competitionId
						},
						{
							resolvers: [
								{
									resolver: competitionWithScoutsResolver,
									fields: ["competition"]
								}
							]
						}
					)
				);
			},
			updateScouts: async (
				competitionId: string,
				scouts: {
					add?: NewScoutModel[];
					remove?: { id: string }[];
					update?: UpdateScoutModel[];
				}
			) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.UPDATE_SCOUTS, {
						competitionId,
						scouts
					})
				);
			},
			inviteScouts: async (competitionId: string, emails: string[]) => {
				return BaseConverter.convert<{ invitations: InvitationModel[] }>(
					await actions.post(API_URLS.COMPETITION.INVITE_SCOUTS, {
						competitionId,
						emails
					})
				);
			},
			updateTeams: async (
				competitionId: string,
				teams: {
					remove: { id: string }[];
				}
			) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.UPDATE_TEAMS, {
						competitionId,
						teams
					})
				);
			},
			getPlayers: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: GetCompetitionPlayersModel }>(
					await actions.get(
						API_URLS.COMPETITION.PLAYERS,
						{
							competitionId
						},
						{
							resolvers: [{ resolver: getCompetitionPlayersResolver, fields: ["competition"] }]
						}
					)
				);
			},
			getPlayersByTeams: async ({
				filters,
				...rest
			}: {
				competitionId: string;
				slim?: boolean;
				filters?: PlayersByTeamsFilterModel;
				withPayments?: boolean;
			} & PaginationParamsModel) => {
				return BaseConverter.convert<{
					competition: GetCompetitionPlayersByTeamsModel;
					nextToken: PaginationTokenType;
					previousToken: PaginationTokenType;
				}>(
					await actions.get(
						API_URLS.COMPETITION.PLAYERS_BY_TEAMS,
						{
							search: filters?.search,
							hasAvatar: filters?.hasAvatar,
							playerStatus: filters?.playerStatus?.length ? filters.playerStatus.join(",") : undefined,
							...rest
						},
						{
							resolvers: [
								{ resolver: getCompetitionPlayersByTeamsResolver, fields: ["competition"] },
								{
									fields: ["nextToken"],
									resolver: paginationTokenResolver
								},
								{
									fields: ["previousToken"],
									resolver: paginationTokenResolver
								}
							]
						}
					)
				);
			},
			getTeamPlayers: async ({
				competitionId,
				teamId,
				filters,
				withPayments
			}: {
				competitionId: string;
				teamId: string;
				filters?: PlayersByTeamsFilterModel;
				withPayments?: boolean;
			}) => {
				return BaseConverter.convert<{
					competition: GetCompetitionTeamPlayersModel;
				}>(
					await actions.get(
						API_URLS.COMPETITION.TEAM_PLAYERS,
						{
							competitionId,
							teamId,
							search: filters?.search,
							hasAvatar: filters?.hasAvatar,
							playerStatus: filters?.playerStatus?.length ? filters.playerStatus.join(",") : undefined,
							withPayments
						},
						{
							resolvers: [{ resolver: getCompetitionTeamPlayersResolver, fields: ["competition"] }]
						}
					)
				);
			},
			deleteCompetition: async (competitionId: string) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.DELETE, {
						competitionId
					})
				);
			},
			generateOpenRegistrationPaymentOrder: async (params: {
				competitionId: string;
				players?: { id: string }[];
				teams?: { id: string; clubId: string }[];
				isPayFullAmount?: boolean;
			}) => {
				return BaseConverter.convert<{ order: { id: string; status: OrderStatus; paymentLink?: string } }>(
					await actions.post(API_URLS.COMPETITION.GENERATE_OPEN_REGISTRATION_PAYMENT_ORDER, params)
				);
			},
			getAudiences: async (competitionId: string) => {
				return BaseConverter.convert<{
					competition: { id: string; audiences: { players: { id: string }[]; teams: { id: string }[] } };
				}>(
					await actions.get(API_URLS.COMPETITION.GET_AUDIENCES, {
						competitionId
					})
				);
			},
			appendToOpenRegistration: async (params: {
				competitionId: string;
				players?: { id: string }[];
				teams?: { id: string; clubId: string; suggestedLevelId?: null | string }[];
			}) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.APPEND_TO_COMPETITION_OPEN_REGISTRATION, params)
				);
			},
			acceptTeams: async (params: {
				competitionId: string;
				club: { id: string; teams?: { id: string; customFee?: number }[] };
				currency?: CurrencyCode;
				partialPayment: null | PartialPaymentSettingsModel;
				feeTaxPaidByClient?: boolean;
			}) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.ACCEPT_COMPETITION_TEAMS, params)
				);
			},
			rejectTeams: async (params: { competitionId: string; teams?: { id: string }[] }) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.REJECT_COMPETITION_TEAMS, params)
				);
			},
			updateClubPlayers: async (params: {
				competitionId: string;
				clubId: string;
				players: {
					update: (Omit<UpdateClubPlayerFormModal, "teams"> & {
						teams: { update?: { id: string; jerseyNumber?: number }[] };
					})[];
				};
			}) => {
				return BaseConverter.convert<{ competition: { id: string } }>(
					await actions.post(API_URLS.COMPETITION.UPDATE_CLUB_PLAYERS, params)
				);
			},
			getHealthDashboard: async (params: { id: string; matchOverdueTime?: number }) => {
				return BaseConverter.convert<{ competition: Partial<CompetitionHealthModel> }>(
					await actions.get(
						API_URLS.COMPETITION.GET_HEALTH_DASHBOARD,
						{
							competitionId: params.id,
							matchOverdueTime: params.matchOverdueTime
						},
						{
							resolvers: [{ resolver: competitionHealthResolver, fields: ["competition"] }]
						}
					)
				);
			},
			getMatchesHealthDashboard: async (params: { id: string; matchOverdueTime?: number }) => {
				return BaseConverter.convert<{ competition: Partial<CompetitionMatchesHealthResultModel> }>(
					await actions.get(
						API_URLS.COMPETITION.GET_MATCHES_HEALTH_DASHBOARD,
						{
							competitionId: params.id,
							matchOverdueTime: params.matchOverdueTime
						},
						{
							resolvers: [{ resolver: competitionMatchesHealthResultResolver, fields: ["competition"] }]
						}
					)
				);
			},
			publishEliminationMatches: async (params: { id: string; divisionId: string }) => {
				return BaseConverter.convert<{ division: { id: string; affectedMatchesCount: number } }>(
					await actions.post(API_URLS.COMPETITION.PUBLISH_ELIMINATION_MATCHES, {
						competitionId: params.id,
						divisionId: params.divisionId
					})
				);
			}
		}),
		[actions]
	);
};

export default useCompetitionApiService;
